<script setup lang="ts">
import { ref, computed } from 'vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import { useGetFetchUnit } from 'src/api/queries/order-portal-bff';
import useDateTime from 'src/composables/useDateTime';
import { NestedContractResponseStatus } from 'src/api/models';
import type { FetchTicketResponse } from 'src/api/models';
import ContractExpandedRow from '../ticket/contract/ContractExpandedRow.vue';
import TicketEditSectionHeader from './TicketEditSectionHeader.vue';

const props = defineProps<{
  ticket: FetchTicketResponse;
  ticketId: string;
}>();

defineEmits<{ 'refresh-ticket': [] }>();

const { getDateDutchFormat } = useDateTime();

const unitId = computed(() => props.ticket?.unit?.id ?? '');

const { data: unitData, isLoading } = useGetFetchUnit(unitId, {
  query: { enabled: computed(() => !!props.ticket?.unit?.id) },
});

const showAll = ref(false);
const expandedRows = ref({});

const displayedContracts = computed(() => {
  const contracts = unitData.value?.contracts ?? [];
  if (showAll.value) return contracts;
  return contracts.filter((c) => c.status !== NestedContractResponseStatus.closed);
});

function formatDate(dateStr: string | undefined): string {
  if (!dateStr) return '--';
  try {
    return getDateDutchFormat(new Date(dateStr));
  } catch {
    return '--';
  }
}

function getStatusClasses(status?: string): string {
  if (status === NestedContractResponseStatus.closed) {
    return 'bg-surface-100 dark:bg-surface-700 text-surface-500 dark:text-surface-400';
  }
  return 'bg-green-50 dark:bg-green-500/10 text-green-700 dark:text-green-400';
}
</script>

<template>
  <div class="p-8">
    <TicketEditSectionHeader title="Contracts" :count="displayedContracts.length" class="mb-4">
      <template #actions>
        <div class="flex items-center gap-0.5">
          <button
            class="filter-btn"
            :class="{ 'filter-btn-active': !showAll }"
            @click="showAll = false"
          >
            Active
          </button>
          <button
            class="filter-btn"
            :class="{ 'filter-btn-active': showAll }"
            @click="showAll = true"
          >
            All
          </button>
        </div>
      </template>
    </TicketEditSectionHeader>

    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <i class="pi pi-spinner pi-spin text-surface-400" style="font-size: 20px" />
    </div>

    <div v-else-if="displayedContracts.length === 0" class="empty-state">
      <i class="pi pi-file" style="font-size: 28px" />
      <p>No contracts found for this unit</p>
    </div>

    <DataTable
      v-else
      v-model:expandedRows="expandedRows"
      :value="displayedContracts"
      data-key="id"
      class="contracts-table"
    >
      <Column expander style="width: 3rem" />
      <Column field="number" header="Number">
        <template #body="{ data }">
          <span class="font-mono text-sm text-surface-700 dark:text-surface-300">{{ data.number }}</span>
        </template>
      </Column>
      <Column field="type" header="Type">
        <template #body="{ data }">
          <span class="text-sm text-surface-600 dark:text-surface-400">{{ data.type }}</span>
        </template>
      </Column>
      <Column header="Status">
        <template #body="{ data }">
          <span
            class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold"
            :class="getStatusClasses(data.status)"
          >
            {{ data.status }}
          </span>
        </template>
      </Column>
      <Column header="Start">
        <template #body="{ data }">
          <span class="text-sm text-surface-600 dark:text-surface-400">{{ formatDate(data.start_date) }}</span>
        </template>
      </Column>
      <Column header="End">
        <template #body="{ data }">
          <span class="text-sm text-surface-600 dark:text-surface-400">{{ formatDate(data.end_date) }}</span>
        </template>
      </Column>

      <template #expansion="slotProps">
        <div class="px-4 py-3">
          <ContractExpandedRow :contract-id="slotProps.data.id" />
        </div>
      </template>
    </DataTable>
  </div>
</template>

<style scoped>
.filter-btn {
  padding: 0.3125rem 0.625rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--p-surface-500);
  transition: all 0.15s;
  cursor: pointer;
}
.filter-btn:hover {
  color: var(--p-surface-700);
  background: var(--p-surface-100);
}
:root[class*='app-dark'] .filter-btn:hover {
  color: var(--p-surface-300);
  background: var(--p-surface-800);
}
.filter-btn-active {
  color: var(--p-primary-700);
  background: var(--p-primary-50);
  font-weight: 600;
}
:root[class*='app-dark'] .filter-btn-active {
  color: var(--p-primary-300);
  background: color-mix(in srgb, var(--p-primary-500) 15%, transparent);
}


.contracts-table {
  border-radius: 0.75rem;
  overflow: hidden;
  border: 1px solid var(--p-surface-200);
}
:root[class*='app-dark'] .contracts-table {
  border-color: var(--p-surface-700);
}
</style>
